import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { Injectable } from "@angular/core";
import { MatSpinner } from "@angular/material/progress-spinner";
import { Observable } from "rxjs";
import { finalize, first } from "rxjs/operators";
import { isObservable, from } from "rxjs";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  Event,
} from "@angular/router";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import { IconName, IconPrefix } from "@fortawesome/free-solid-svg-icons";
import { fal } from "@fortawesome/pro-light-svg-icons";
import { find, get } from "lodash";

const OLD_FA_ICONS_MAP: {
  name: string;
  newIconString: string;
  newIcon?: [IconPrefix, IconName];
}[] = [
  { name: "fa-beer", newIconString: "fal beer" },
  { name: "fa-star-o", newIconString: "fal star" },
  { name: "fa-cocktail", newIconString: "fal cocktail" },
  { name: "fa-map", newIconString: "fal map" },
  { name: "fa-certificate", newIconString: "fal certificate" },
  { name: "fa-tag", newIconString: "fal tag" },
  { name: "fa-moon-o", newIconString: "fal moon" },
  { name: "fa-globe", newIconString: "fal globe-americas" },
  { name: "fa-hotel", newIconString: "fal hotel" },
  { name: "fa-map-marker", newIconString: "fal map-marker-alt" },
  { name: "fa-cutlery", newIconString: "fal utensils" },
  { name: "fa-dice", newIconString: "fal dice" },
  { name: "fa-exclamation", newIconString: "fal exclamation" },
  { name: "fa-theater-masks", newIconString: "fal theater-masks" },
  { name: "fa-utensils", newIconString: "fal utensils" },
  { name: "fa-glass", newIconString: "fal glass-martini" },
  { name: "fa-music", newIconString: "fal music" },
  { name: "fa-heart", newIconString: "fal heart" },
  { name: "fa-money", newIconString: "fal money-bill" },
  { name: "fa-location-arrow", newIconString: "fal location-arrow" },
  { name: "fa-smile-o", newIconString: "fal smile" },
  { name: "fa-building", newIconString: "fal building" },
  { name: "fa-road", newIconString: "fal road" },
  { name: "fa-bed", newIconString: "fal bed" },
  { name: "fa-calendar", newIconString: "fal calendar" },
  { name: "fa-arrow-circle-o-up", newIconString: "fal arrow-circle-up" },
  { name: "fa-star", newIconString: "fal star" },
  { name: "fa-car", newIconString: "fal car" },
  { name: "fa-ticket", newIconString: "fal ticket-alt" },
  { name: "fa-bell", newIconString: "fal bell" },
];

@Injectable({
  providedIn: "root",
})
export class UiService {
  private overlayRef: OverlayRef = this.createOverlay();
  private obsQueueCount = 0;
  oldFAIconsMap!: {
    name: string;
    newIconString: string;
    newIcon?: [IconPrefix, IconName];
  }[];
  faIcons!: { name: string; icon: [IconPrefix, IconName] }[];
  constructor(
    private overlay: Overlay,
    private router: Router,
    private library: FaIconLibrary,
  ) {
    this.watchRouteNavigationChanges();
    this.initFAIcons();
  }

  private initFAIcons() {
    OLD_FA_ICONS_MAP.forEach((icon) => {
      icon.newIcon = icon.newIconString!.split(" ") as [IconPrefix, IconName];
    });

    this.oldFAIconsMap = OLD_FA_ICONS_MAP;
    this.library.addIconPacks(fal);

    const _availableIcons: { name: string; icon: [IconPrefix, IconName] }[] =
      [];
    //@ts-ignore
    for (let key of Object.keys(this.library.definitions)) {
      //@ts-ignore
      for (let defKey of Object.keys(this.library.definitions[key])) {
        //@ts-ignore
        const name = `${this.library.definitions[key][defKey].prefix} ${this.library.definitions[key][defKey].iconName}`;
        _availableIcons.push({
          name,
          icon: name.split(" ") as [IconPrefix, IconName],
        });
      }
    }

    this.faIcons = _availableIcons;
  }

  private createOverlay() {
    return this.overlay.create({
      hasBackdrop: true,
      backdropClass: "loader-backdrop",
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
    });
  }

  private increaseObsQueue() {
    this.obsQueueCount++;
    // Only attach if its the first obs/promise to await
    if (this.obsQueueCount === 1) {
      this.overlayRef.attach(new ComponentPortal(MatSpinner));
    }
  }

  private decreaseObsQueue() {
    this.obsQueueCount--;
    // Only detach once last obs/promise is completed
    if (this.obsQueueCount === 0) {
      this.overlayRef.detach();
    }
  }

  private watchRouteNavigationChanges() {
    this.router.events.subscribe((event: Event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.increaseObsQueue();
          break;
        }
        case event instanceof NavigationEnd: {
          this.decreaseObsQueue();
          break;
        }
        case event instanceof NavigationCancel: {
          this.decreaseObsQueue();
          break;
        }
        case event instanceof NavigationError: {
          this.decreaseObsQueue();
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  waitForEvent(event: Observable<any> | Promise<any>) {
    this.increaseObsQueue();
    // TODO: We're assuming we only want the first value from the api, revisit this to account for streams

    if (isObservable(event)) {
      return event.pipe(
        first(),
        finalize(() => this.decreaseObsQueue()),
      );
    }
    // Assume its a promise if it has a then function
    else if (!!event.then) {
      return from(event).pipe(
        first(),
        finalize(() => this.decreaseObsQueue()),
      );
    }

    return from(new Promise((resolve) => resolve(null))).pipe(
      first(),
      finalize(() => this.decreaseObsQueue()),
    );
  }

  registrationStatusOptions = [
    { name: "Registered", value: "registered" },
    { name: "Anonymous", value: "anonymous" },
    { name: "Registered and Anonymous", value: "all" },
  ];

  physicalPresenceOptions = [
    { name: "On Property", value: "on_property" },
    { name: "Off Property", value: "off_property" },
    { name: "On and Off Property", value: "all" },
  ];

  locationPermissionOptions = [
    { name: "All", value: "all" },
    { name: "Unknown", value: "unknown" },
    { name: "Always Allowed", value: "always" },
    { name: "When In App", value: "partial" },
    { name: "Denied", value: "denied" },
  ];

  notificationPermissionOptions = [
    { name: "All", value: "all" },
    { name: "Unknown", value: "unknown" },
    { name: "Granted", value: "granted" },
    { name: "Denied", value: "denied" },
  ];

  blutetoothPermissionOptions = [
    { name: "All", value: "all" },
    { name: "Unknown", value: "unknown" },
    { name: "Granted", value: "granted" },
    { name: "Denied", value: "denied" },
  ];

  conditionals: any = {
    "Object": [],

    "Object[]": [],

    "Date": [
      {
        value: "eq",
        operator: "===",
        name: "Equal to",
      },
      {
        value: "neq",
        operator: "!==",
        name: "Not Equal to",
      },
      {
        value: "gt",
        operator: ">",
        name: "After",
      },
      {
        value: "lt",
        operator: "<",
        name: "Prior to",
      },
    ],

    "String": [
      {
        value: "eq",
        operator: "===",
        name: "Equals/Is",
      },
      {
        value: "neq",
        operator: "!==",
        name: "Does Not Equal/Is Not",
      },
      {
        value: "contains",
        operator: "contains",
        name: "Contains",
      },
      {
        value: "starts-with",
        operator: "startsWith",
        name: "Starts With",
      },
      {
        value: "ends-with",
        operator: "endsWith",
        name: "Ends With",
      },
    ],

    "Boolean": [
      {
        value: "eq",
        operator: "===",
        name: "Equals",
      }
    ],

    "locationPermission": [
      {
        value: "eq",
        operator: "===",
        name: "Includes",
      }
    ],

    "dayOfWeek": [
      {
        value: "eq",
        operator: "===",
        name: "Includes",
      }
    ],

    "timeOfDay": [
      {
        name: "Is before",
        operator: "before",
        value: "before",
      },
      {
        name: "Is between",
        operator: "between",
        value: "between",
      },
      {
        name: "Is after",
        operator: "after",
        value: "after",
      },
    ],

    "lastVisit": [
      {
        value: "gt",
        operator: ">",
        name: "Greater Than",
      },
      {
        value: "gte",
        operator: ">=",
        name: "Greater Than or Equal To",
      },
      {
        value: "lt",
        operator: "<",
        name: "Less Than",
      },
      {
        value: "lte",
        operator: "<=",
        name: "Less Than or Equal To",
      },
    ],

    "timeOnStation": [
      {
        value: "gt",
        operator: ">",
        name: "Greater Than",
      },
      {
        value: "gte",
        operator: ">=",
        name: "Greater Than or Equal To",
      },
      {
        value: "lt",
        operator: "<",
        name: "Less Than",
      },
      {
        value: "lte",
        operator: "<=",
        name: "Less Than or Equal To",
      },
    ],

    "History": [
      {
        value: "all",
        name: "All Time",
      },
      {
        value: "range",
        name: "Date Range",
      },
    ],

    "Number": [
      {
        value: "eq",
        operator: "===",
        name: "Equals/Is",
      },
      {
        value: "neq",
        operator: "!==",
        name: "Does Not Equal/Is Not",
      },
      {
        value: "gt",
        operator: ">",
        name: "Greater Than",
      },
      {
        value: "gte",
        operator: ">=",
        name: "Greater Than or Equal To",
      },
      {
        value: "lt",
        operator: "<",
        name: "Less Than",
      },
      {
        value: "lte",
        operator: "<=",
        name: "Less Than or Equal To",
      },
    ],

    "Array": [
      {
        value: "any",
        operator: "any",
        name: "Any Of",
      },
      {
        value: "all",
        operator: "all",
        name: "All",
      },
    ],

    "String[]": [
      {
        value: "any",
        operator: "any",
        name: "Any Of",
      },
      {
        value: "all",
        operator: "all",
        name: "All",
      },
    ],

    "Integer[]": [
      {
        value: "any",
        operator: "any",
        name: "Any Of",
      },
      {
        value: "all",
        operator: "all",
        name: "All",
      },
    ],

    "Number[]": [
      {
        value: "any",
        operator: "any",
        name: "Any Of",
      },
      {
        value: "all",
        operator: "all",
        name: "All",
      },
    ],

    "Integer": [
      {
        value: "eq",
        operator: "===",
        name: "Equals/Is",
      },
      {
        value: "neq",
        operator: "!==",
        name: "Does Not Equal/Is Not",
      },
      {
        value: "gt",
        operator: ">",
        name: "Greater Than",
      },
      {
        value: "gte",
        operator: ">=",
        name: "Greater Than or Equal To",
      },
      {
        value: "lt",
        operator: "<",
        name: "Less Than",
      },
      {
        value: "lte",
        operator: "<=",
        name: "Less Than or Equal To",
      },
    ],

    "Float": [
      {
        value: "eq",
        operator: "===",
        name: "Equals/Is",
      },
      {
        value: "neq",
        operator: "!==",
        name: "Does Not Equal/Is Not",
      },
      {
        value: "gt",
        operator: ">",
        name: "Greater Than",
      },
      {
        value: "gte",
        operator: ">=",
        name: "Greater Than or Equal To",
      },
      {
        value: "lt",
        operator: "<",
        name: "Less Than",
      },
      {
        value: "lte",
        operator: "<=",
        name: "Less Than or Equal To",
      },
    ],
  };

  stringForCatalogFilter(op: any) {
    let cond = find(get(this.conditionals, op.type) as Array<any>, {value: op.operator});
    return cond?.name || op.operator;
  }

  stringForFilterRegistrationStatus(val: any) {
    if(val == null || val == undefined || val == ""){
      return "-";
    }
    var opt = find(this.registrationStatusOptions, {value: val});
    return opt?.name || "?";
  }

  stringForFilterOnOffProp(val: any) {
    if(val == null || val == undefined || val == ""){
      return "-";
    }
    var opt = find(this.physicalPresenceOptions, {value: val});
    return opt?.name || "?";
  }

  stringForFilterLocationPermission(val: any) {
    if(val == null || val == undefined || val == ""){
      return "-";
    }
    var opt = find(this.locationPermissionOptions, {value: val});
    return opt?.name || "?";
  }

  stringForFilterNotificationPermission(val: any) {
    if(val == null || val == undefined || val == ""){
      return "-";
    }
    var opt = find(this.notificationPermissionOptions, {value: val});
    return opt?.name || "?";
  }

  stringForFilterBluetoothPermission(val: any) {
    if(val == null || val == undefined || val == ""){
      return "-";
    }
    var opt = find(this.blutetoothPermissionOptions, {value: val});
    return opt?.name || "?";
  }

  stringForFilterLocations(val:any, geofences: any, beaconregions: any) {
    if(val == null || val == undefined || val.length == 0){
      return "-";
    }
    let ret = val.map((loc: any)=>{
      var objType = loc.split(':')[0];
      var objId = loc.split(':')[1];
      var obj = find(objType == "geofence" ? geofences : beaconregions, {id: parseInt(objId)});
      return obj?.name || "?";
    })
    return ret.join(", ");
  }

}
